// ─────────────────────────────────────────────────────────────────────────────
// OpportunityModal, desktop Add / Edit / Delete for a manual opportunity.
//
// The legacy desktop dashboard's Opportunities "Add" / "Edit" / "Delete" were
// never wired in the React port (dead buttons). This modal restores them, using
// the SAME persistence the mobile flow uses: a manual opp lives in
// /api/data/opportunities and (when dated) is mirrored into /api/data/schedule
// as its type (lib upsertOpportunity / deleteOpportunityEverywhere). Delete
// scrubs BOTH blobs so a removed opp can't ghost on another surface.
//
// Scope: the core fields (type, title, date, time, end, address, money, notes).
// Existing invitees/responses/createdById are preserved untouched on edit; a new
// invitee picker is a separate follow-up (the card still renders invitee avatars
// from whatever the opp already carries).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import { upsertOpportunity, deleteOpportunityEverywhere, fetchContacts } from '../../lib';
import type { Opportunity, OpportunityType, OpportunityInvitee } from '../../lib';
import type { Contact } from '../../lib/types';
import './OpportunityModal.css';

/* opp-invitees 2026-07-06 (Ryan): the picker mobile always had, ported to
   desktop. Contacts autocomplete → invitee chips; stores the SAME
   OpportunityInvitee shape mobile writes (name/userId/contactId/email/avatar),
   so both surfaces and the Upcoming card's avatar map stay compatible. */
function contactDisplayName(c: Contact): string {
  const full = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
  return full || c.bandName || c.email || '';
}
function contactToInvitee(c: Contact): OpportunityInvitee {
  return {
    name: contactDisplayName(c),
    bandName: c.bandName,
    userId: c.userId ?? c.teamUserId ?? undefined,
    contactId: c.id,
    email: c.email,
    avatarUrl: c.avatarUrl || c.avatar_url || c.photoUrl || c.photo_url || undefined,
  };
}
function inviteeKey(i: OpportunityInvitee): string {
  return String(i.userId ?? i.user_id ?? i.contactId ?? i.id ?? i.email ?? i.name ?? '');
}
function inviteeAvatar(i: OpportunityInvitee): string {
  return i.avatarUrl || i.avatar_url || i.photoUrl || '';
}
function contactAvatar(c: Contact): string {
  return c.avatarUrl || c.avatar_url || c.photoUrl || c.photo_url || '';
}
/** Tiny round avatar (or initial-letter tile when no photo) for chips/suggestions. */
function AvatarDot({ src, name, size }: { readonly src: string; readonly name: string; readonly size: number }): JSX.Element {
  const style = { width: size, height: size, borderRadius: '50%', flexShrink: 0 } as const;
  if (src) return <img src={src} alt="" style={{ ...style, objectFit: 'cover' }} />;
  return (
    <span
      style={{
        ...style,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#e9dfdf',
        color: '#8b1a2b',
        font: `700 ${Math.round(size * 0.5)}px/1 Inter, sans-serif`,
      }}
    >
      {(name || '?').trim().charAt(0).toUpperCase()}
    </span>
  );
}

export interface OpportunityModalProps {
  /** The opp being edited, or null to create a new one. */
  readonly opp: Opportunity | null;
  /** Current user id, stamped as createdById on a new opp (delete semantics). */
  readonly currentUserId?: string | number | null;
  readonly onClose: () => void;
  /** Called after a successful save/delete so the dashboard can refetch. */
  readonly onChanged: () => void;
}

// The user-creatable manual opp types (mirrors mobile OPP_TYPES).
const TYPE_OPTIONS: ReadonlyArray<{ id: OpportunityType; label: string }> = [
  { id: 'rehearsal', label: 'Rehearsal' },
  { id: 'content', label: 'Content' },
  { id: 'write', label: 'Write' },
  { id: 'pr', label: 'PR' },
];
const PR_SUB_TYPES = ['Podcast', 'Radio', 'Meeting', 'Other'];

function newOppId(): string {
  return 'opp_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
}

export function OpportunityModal({
  opp,
  currentUserId,
  onClose,
  onChanged,
}: OpportunityModalProps): JSX.Element {
  const isEdit = !!opp;
  const [type, setType] = useState<OpportunityType>((opp?.type as OpportunityType) || 'rehearsal');
  const [prType, setPrType] = useState(opp?.prType || PR_SUB_TYPES[0]);
  const [title, setTitle] = useState(opp?.title || '');
  const [date, setDate] = useState(opp?.date || '');
  const [time, setTime] = useState(opp?.time || '');
  const [endTime, setEndTime] = useState(opp?.endTime || '');
  const [address, setAddress] = useState(opp?.address || '');
  const [money, setMoney] = useState(opp?.money != null ? String(opp.money) : '');
  const [notes, setNotes] = useState(opp?.notes || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // opp-invitees 2026-07-06
  const [invitees, setInvitees] = useState<ReadonlyArray<OpportunityInvitee>>(opp?.invitees ?? []);
  const [contacts, setContacts] = useState<ReadonlyArray<Contact>>([]);
  const [invQuery, setInvQuery] = useState('');

  useEffect(() => {
    let live = true;
    void fetchContacts().then((c) => { if (live) setContacts(c); });
    return () => { live = false; };
  }, []);

  const invSuggestions = invQuery.trim()
    ? contacts
        .filter((c) => {
          const label = (contactDisplayName(c) + ' ' + (c.bandName || '')).toLowerCase();
          return label.includes(invQuery.trim().toLowerCase());
        })
        .filter((c) => !invitees.some((i) => inviteeKey(i) === inviteeKey(contactToInvitee(c))))
        .slice(0, 6)
    : [];

  function addInvitee(c: Contact): void {
    setInvitees((prev) => [...prev, contactToInvitee(c)]);
    setInvQuery('');
  }
  function removeInvitee(key: string): void {
    setInvitees((prev) => prev.filter((i) => inviteeKey(i) !== key));
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  async function save(): Promise<void> {
    if (!title.trim() && !address.trim()) {
      setErr('Add a title or an address.');
      return;
    }
    setBusy(true);
    setErr('');
    // Preserve the untouched fields on edit (invitees/responses/createdById).
    const base: Opportunity = opp ?? { id: newOppId(), createdById: currentUserId ?? undefined };
    const next: Opportunity = {
      ...base,
      type,
      title: title.trim(),
      date: date || undefined,
      time: time || null,
      endTime: endTime || null,
      address: address.trim() || null,
      money: money.trim() || undefined,
      notes: notes.trim(),
      prType: type === 'pr' ? prType : undefined,
      invitees, // opp-invitees 2026-07-06: picker output (responses untouched)
    };
    try {
      await upsertOpportunity(next);
      onChanged();
      onClose();
    } catch {
      setErr('Save failed. Try again.');
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!opp) return;
    if (!window.confirm('Delete this opportunity? This removes it from the dashboard and the calendar. Cannot be undone.')) return;
    setBusy(true);
    setErr('');
    try {
      await deleteOpportunityEverywhere(String(opp.id));
      onChanged();
      onClose();
    } catch {
      setErr('Delete failed. Try again.');
      setBusy(false);
    }
  }

  return createPortal(
    <div className="opm-overlay" role="presentation">
      <div className="opm-backdrop" onClick={() => { if (!busy) onClose(); }} />
      <div className="opm-modal" role="dialog" aria-label={isEdit ? 'Edit opportunity' : 'New opportunity'}>
        <header className="opm-head">
          <div className="opm-title">{isEdit ? 'Edit opportunity' : 'New opportunity'}</div>
          <button className="opm-close" type="button" aria-label="Close" onClick={onClose} disabled={busy}>
            ×
          </button>
        </header>

        <div className="opm-body">
          <div className="opm-field">
            <label className="opm-label">Type</label>
            <select className="opm-input" value={type} onChange={(e) => setType(e.target.value as OpportunityType)}>
              {TYPE_OPTIONS.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
              {/* Preserve a non-creatable type when editing one. */}
              {!TYPE_OPTIONS.some((t) => t.id === type) && <option value={type}>{type}</option>}
            </select>
          </div>

          {type === 'pr' && (
            <div className="opm-field">
              <label className="opm-label">PR type</label>
              <select className="opm-input" value={prType} onChange={(e) => setPrType(e.target.value)}>
                {PR_SUB_TYPES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          )}

          <div className="opm-field">
            <label className="opm-label">Title</label>
            <input className="opm-input" type="text" value={title} placeholder="e.g. Full-band rehearsal" onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="opm-row">
            <div className="opm-field">
              <label className="opm-label">Date</label>
              <input className="opm-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="opm-field">
              <label className="opm-label">Start</label>
              <input className="opm-input" type="time" value={time || ''} onChange={(e) => setTime(e.target.value)} />
            </div>
            <div className="opm-field">
              <label className="opm-label">End</label>
              <input className="opm-input" type="time" value={endTime || ''} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>

          <div className="opm-field">
            <label className="opm-label">Address</label>
            <input className="opm-input" type="text" value={address} placeholder="Venue or address" onChange={(e) => setAddress(e.target.value)} />
          </div>

          <div className="opm-field">
            <label className="opm-label">Money (optional)</label>
            <input className="opm-input" type="text" value={money} placeholder="Guarantee or note (e.g. $500, door split)" onChange={(e) => setMoney(e.target.value)} />
          </div>

          <div className="opm-field">
            <label className="opm-label">Team members</label>
            {invitees.length > 0 && (
              <div className="opm-chips">
                {invitees.map((i) => {
                  const k = inviteeKey(i);
                  return (
                    <span key={k} className="opm-chip">
                      <AvatarDot src={inviteeAvatar(i)} name={i.name || ''} size={18} />
                      {i.name || i.email || 'Member'}
                      <button
                        type="button"
                        className="opm-chip__x"
                        aria-label={'Remove ' + (i.name || 'invitee')}
                        onClick={() => removeInvitee(k)}
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <input
              className="opm-input"
              type="text"
              value={invQuery}
              placeholder="Type a contact name…"
              autoComplete="off"
              onChange={(e) => setInvQuery(e.target.value)}
            />
            {invSuggestions.length > 0 && (
              <div className="opm-suggest">
                {invSuggestions.map((c) => (
                  <button
                    key={String(c.id ?? c.userId ?? c.email)}
                    type="button"
                    className="opm-suggest__item"
                    onClick={() => addInvitee(c)}
                  >
                    <AvatarDot src={contactAvatar(c)} name={contactDisplayName(c)} size={24} />
                    <span>
                      {contactDisplayName(c)}
                      {c.bandName ? <span className="opm-suggest__sub"> · {c.bandName}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="opm-field">
            <label className="opm-label">Notes</label>
            <textarea className="opm-input opm-textarea" value={notes} rows={3} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {err && <div className="opm-msg opm-msg--err">{err}</div>}
        </div>

        <footer className="opm-foot">
          {isEdit ? (
            <button className="opm-btn opm-btn--danger" type="button" disabled={busy} onClick={() => void remove()}>
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="opm-foot__right">
            <button className="opm-btn" type="button" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button className="opm-btn opm-btn--primary" type="button" disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving…' : isEdit ? 'Save' : 'Add'}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
